import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";

/**
 * Filters actionVerbAnalysis to include only verbs that are the first word of a sentence
 * @param analysis The complete resume analysis object
 * @returns A new analysis with filtered action verbs
 */
export function removeExtraWeakVerbs(analysis: ResumeAnalysis): ResumeAnalysis {
  if (!analysis) return analysis;

  // Create a deep copy to avoid mutating the original object
  const filteredAnalysis: ResumeAnalysis = JSON.parse(JSON.stringify(analysis));

  // Process education section
  if (filteredAnalysis.educations && filteredAnalysis.educations.individualEducationsAnalysis) {
    filteredAnalysis.educations.individualEducationsAnalysis.forEach(education => {
      if (education.descriptionAnalysis) {
        education.descriptionAnalysis.forEach(desc => {
          if (desc.analysis && desc.analysis.actionVerbAnalysis) {
            // Filter to keep only verbs that are the first word of the sentence
            desc.analysis.actionVerbAnalysis = desc.analysis.actionVerbAnalysis.filter(verb => {
              const statement = desc.statement.trim();
              const firstWord = statement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
              return firstWord === verb.verb.toLowerCase();
            });
          }
        });
      }
    });
  }

  // Process work experience section
  if (filteredAnalysis.workExperiences && filteredAnalysis.workExperiences.individualWorkExperiencesAnalysis) {
    filteredAnalysis.workExperiences.individualWorkExperiencesAnalysis.forEach(experience => {
      if (experience.descriptionAnalysis) {
        experience.descriptionAnalysis.forEach(desc => {
          if (desc.analysis && desc.analysis.actionVerbAnalysis) {
            desc.analysis.actionVerbAnalysis = desc.analysis.actionVerbAnalysis.filter(verb => {
              const statement = desc.statement.trim();
              const firstWord = statement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
              return firstWord === verb.verb.toLowerCase();
            });
          }
        });
      }
    });
  }

  // Process projects section
  if (filteredAnalysis.projects && filteredAnalysis.projects.individualProjectsAnalysis) {
    filteredAnalysis.projects.individualProjectsAnalysis.forEach(project => {
      if (project.descriptionAnalysis) {
        project.descriptionAnalysis.forEach(desc => {
          if (desc.analysis && desc.analysis.actionVerbAnalysis) {
            desc.analysis.actionVerbAnalysis = desc.analysis.actionVerbAnalysis.filter(verb => {
              const statement = desc.statement.trim();
              const firstWord = statement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
              return firstWord === verb.verb.toLowerCase();
            });
          }
        });
      }
    });
  }

  // Process skills section if it has descriptions
  if (filteredAnalysis.skills && filteredAnalysis.skills.descriptionAnalysis) {
    filteredAnalysis.skills.descriptionAnalysis.forEach(desc => {
      if (desc.analysis && desc.analysis.actionVerbAnalysis) {
        desc.analysis.actionVerbAnalysis = desc.analysis.actionVerbAnalysis.filter(verb => {
          const statement = desc.statement.trim();
          const firstWord = statement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
          return firstWord === verb.verb.toLowerCase();
        });
      }
    });
  }

  // Process certifications section if available
  if (filteredAnalysis.certifications && filteredAnalysis.certifications.individualCertificatesAnalysis) {
    filteredAnalysis.certifications.individualCertificatesAnalysis.forEach(cert => {
      if (cert.descriptionAnalysis) {
        cert.descriptionAnalysis.forEach(desc => {
          if (desc.analysis && desc.analysis.actionVerbAnalysis) {
            desc.analysis.actionVerbAnalysis = desc.analysis.actionVerbAnalysis.filter(verb => {
              const statement = desc.statement.trim();
              const firstWord = statement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
              return firstWord === verb.verb.toLowerCase();
            });
          }
        });
      }
    });
  }

  // Process any additional sections with descriptions
  // Add more sections here if needed

  return filteredAnalysis;
}

/**
 * Helper function that can be used to filter action verbs for a specific description
 * @param statement The text statement to analyze
 * @param actionVerbAnalysis The array of action verb analysis objects
 * @returns Filtered array containing only verbs that are the first word
 */
export function filterFirstWordVerbs(statement: string, actionVerbAnalysis: any[]) {
  if (!statement || !actionVerbAnalysis) return [];
  
  const trimmedStatement = statement.trim();
  const firstWord = trimmedStatement.split(' ')[0].toLowerCase().replace(/[^\w]/g, '');
  
  return actionVerbAnalysis.filter(verb => 
    firstWord === verb.verb.toLowerCase()
  );
}